import { useState, useCallback, useEffect, useRef } from 'react';
import type { ChatMessageData } from '../components/chat/types';
import { usePromptTranslations } from '../i18n';

export interface ContentVersionDTO {
    id: string;
    content_id: string;
    version: number;
    prompt: string;
    metadata: Record<string, unknown> | null;
    job_id: string | null;
    is_current: boolean;
    created_at: string;
}

export interface RollbackApiResult {
    job_id?: string;
    content_id?: string;
    version?: number;
}

export interface GenerationHistoryApi {
    listVersions: (contentId: string) => Promise<ContentVersionDTO[]>;
    rollback: (contentId: string, version: number) => Promise<RollbackApiResult>;
}

export type GenerationHistoryMode = 'form' | 'chat';

export interface UseGenerationHistoryReturn {
    mode: GenerationHistoryMode;
    messages: ChatMessageData[];
    isLoading: boolean;
    isRollbackLoading: boolean;
    rootContentId: string | null;
    setRootContentId: (id: string | null) => void;
    switchToChat: (contentId: string) => void;
    refreshHistory: () => Promise<void>;
    handleRollback: (version: number) => Promise<RollbackApiResult | undefined>;
    appendGeneration: (
        prompt: string,
        metadata?: Record<string, unknown> | null,
        version?: number
    ) => void;
    /**
     * Resolve the most recent pending assistant message after a job fails.
     * (The success path doesn't need this — refreshHistory() already
     * replaces the whole message list with the authoritative server data.)
     */
    resolvePendingGeneration: (errorMessage?: string) => void;
}

function versionsToMessages(versions: ContentVersionDTO[]): ChatMessageData[] {
    const sorted = [...versions].sort((a, b) => a.version - b.version);
    const messages: ChatMessageData[] = [];

    for (const v of sorted) {
        messages.push({
            id: `user-v${v.version}`,
            role: 'user',
            content: v.prompt,
            metadata: v.metadata,
            version: v.version,
            timestamp: v.created_at,
            isCurrent: v.is_current,
        });

        messages.push({
            id: `ai-v${v.version}`,
            role: 'assistant',
            content: v.is_current
                ? 'Tôi đã tạo câu hỏi dựa trên cấu hình của bạn.'
                : `Đã tạo phiên bản ${v.version} dựa trên cấu hình của bạn.`,
            version: v.version,
            timestamp: v.created_at,
            isCurrent: v.is_current,
        });
    }
    return messages;
}

export function useGenerationHistory(api?: GenerationHistoryApi): UseGenerationHistoryReturn {
    const pt = usePromptTranslations();
    const [mode, setMode] = useState<GenerationHistoryMode>('form');
    const [messages, setMessages] = useState<ChatMessageData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRollbackLoading, setIsRollbackLoading] = useState(false);
    const [rootContentId, setRootContentId] = useState<string | null>(null);
    const fetchedRef = useRef(false);

    const refreshHistory = useCallback(async () => {
        if (!rootContentId || !api) return;
        setIsLoading(true);
        try {
            const versions = await api.listVersions(rootContentId);
            setMessages(versionsToMessages(versions));
            if (versions.length > 0) {
                setMode('chat');
            }
        } catch {
            // non-fatal: keep current messages
        } finally {
            setIsLoading(false);
        }
    }, [rootContentId, api]);

    useEffect(() => {
        if (rootContentId && api && !fetchedRef.current) {
            fetchedRef.current = true;
            refreshHistory();
        }
    }, [rootContentId, api, refreshHistory]);

    useEffect(() => {
        fetchedRef.current = false;
    }, [rootContentId]);

    const switchToChat = useCallback((contentId: string) => {
        setRootContentId(contentId);
        setMode('chat');
    }, []);

    const appendGeneration = useCallback(
        (prompt: string, metadata?: Record<string, unknown> | null, version?: number) => {
            const now = new Date().toISOString();
            const v = version ?? messages.length / 2 + 1;

            setMessages((prev) => [
                ...prev.map((m) => ({ ...m, isCurrent: false })),
                {
                    id: `user-v${v}`,
                    role: 'user',
                    content: prompt,
                    metadata: metadata ?? null,
                    version: v,
                    timestamp: now,
                    isCurrent: true,
                },
                {
                    // The job has only just been created here, not completed —
                    // show a "generating..." placeholder, not a success claim.
                    // Once the job actually completes, refreshHistory() replaces
                    // this whole array with the authoritative server data
                    // (see the jobStatus 'completed' effect in AIContentGenerator).
                    id: `ai-v${v}`,
                    role: 'assistant',
                    content: pt.generating,
                    version: v,
                    timestamp: now,
                    isCurrent: true,
                    status: 'pending',
                },
            ]);
        },
        [messages.length, pt.generating]
    );

    const resolvePendingGeneration = useCallback(
        (errorMessage?: string) => {
            setMessages((prev) => {
                let idx = -1;
                for (let i = prev.length - 1; i >= 0; i--) {
                    if (prev[i].role === 'assistant' && prev[i].status === 'pending') {
                        idx = i;
                        break;
                    }
                }
                if (idx === -1) return prev;
                const next = [...prev];
                next[idx] = {
                    ...next[idx],
                    content: errorMessage || pt.generationFailed,
                    status: 'failed',
                };
                return next;
            });
        },
        [pt.generationFailed]
    );

    const handleRollback = useCallback(
        async (version: number): Promise<RollbackApiResult | undefined> => {
            if (!rootContentId || !api) return undefined;
            setIsRollbackLoading(true);
            try {
                const result = await api.rollback(rootContentId, version);
                return result;
            } catch {
                return undefined;
            } finally {
                setIsRollbackLoading(false);
            }
        },
        [rootContentId, api]
    );

    return {
        mode,
        messages,
        isLoading,
        isRollbackLoading,
        rootContentId,
        setRootContentId,
        switchToChat,
        refreshHistory,
        handleRollback,
        appendGeneration,
        resolvePendingGeneration,
    };
}
